(ns minorodata.components.tables.core
  (:require ["@radix-ui/react-scroll-area" :as RadixScrollArea]
            ["ag-grid-react" :refer [AgGridReact]]
            ["react" :as react]
            [applied-science.js-interop :as jsi]
            [clojure.string :as str]
            [lucide-react :refer [Check ChevronDown RefreshCw Search]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.utils :refer [format-date]]
            [ui.data-grid.column-resize :as column-resize]
            [ui.data-grid.condition-value-filter :as condition-value-filter]
            [ui.data-grid.core]
            [ui.data-grid.theme :as ag-theme]
            [minorodata.components.spinner.core :refer [spinner-lucide]]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :as rt.easy]
            [ui.accordion :refer [accordion-content accordion-item
                                  accordion-root accordion-trigger]]
            [ui.button :refer [button]]
            [ui.date-time :refer [date-tooltip]]
            [ui.fields :as fields :refer [checkbox select-trigger-class]]
            [ui.page :refer [page-content page-controls page-header page-root
                             page-title]]
            [ui.popover :refer [popover-content popover-root popover-trigger]]
            [ui.tooltip :refer [tooltip]]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))

(def ^:private table-columns
  "Column metadata in grid order. `:label` drives the \"Select fields\"
   filter-panel checkbox text and the initial header-visibility order;
   `:column-id` is the matching AG Grid colId — keep both in lockstep by
   editing this single list rather than two parallel ones. `:label` may
   differ from the grid's `headerName` (e.g. \"Database\" vs \"Catalog\")."
  [{:label "Table name" :column-id "table"}
   {:label "Unit tests" :column-id "unit-tests"}
   {:label "Schema" :column-id "schema"}
   {:label "Database" :column-id "catalog"}
   {:label "Created at" :column-id "created"}
   ;; "Updated at" intentionally omitted
   {:label "Primary keys" :column-id "primary-keys"}
   {:label "Row count" :column-id "row-count"}
   {:label "Ext. connect." :column-id "external-connection"}
   {:label "Output of extracts" :column-id "extracts"}
   {:label "Output of sources" :column-id "sources"}
   {:label "Output of transforms" :column-id "transforms"}
   {:label "Output of transform groups" :column-id "groups"}])

(def default-headers (mapv :label table-columns))
(def ^:private table-column-ids (mapv :column-id table-columns))
(def ^:private details-column-ids (set (take 8 table-column-ids)))

(defn init-tables []
  (dispatch [:tables-metadata.events/init]))

(defn ->schemas [tables]
  (->> tables
       (map #(str (:catalog %) "." (:schema %)))
       set
       (reduce (fn [acc full-name]
                 (assoc acc full-name true))
               {})))

(defn use-table-filters []
  (let [tables @(re-posh/subscribe [:tables-metadata/prop :tables-metadata/tables])
        [filters set-filters] (react/useState {:headers (->> default-headers
                                                             (map-indexed (fn [i name]
                                                                            (-> [name {:enabled true
                                                                                       :order   i}])))
                                                             (into {}))
                                               :query   ""
                                               :schemas nil})

        filtered-tables (react/useMemo
                         (fn []
                           (vec
                            (cond->> tables
                              (not-empty (:query filters))
                              (filter (fn [t] (str/includes? (str/lower-case (:table t)) (str/lower-case (:query filters)))))

                              (and (not-empty (:schemas filters))
                                   (some false? (vals (:schemas filters))))
                              (filter (fn [t]
                                        (let [full-name (str (:catalog t) "." (:schema t))]
                                          (get (:schemas filters) full-name)))))))
                         #js [tables (:query filters) (:schemas filters)])]
    (react/useEffect
     (fn []
       (when (empty? tables)
         (init-tables))
       (fn []))
     #js [])

    (react/useEffect
     (fn []
       (when (not-empty tables)
         (when (not (:schemas filters))
           (set-filters #(assoc % :schemas (->schemas filtered-tables))))

         (when (not (:headers filters))
           (init-tables)))
       (fn []))
     #js [tables])

    {:filters     filters
     :tables      filtered-tables
     :set-filters set-filters
     :set-query   (fn [q] (set-filters #(assoc % :query q)))
     :set-headers (fn [k v] (set-filters #(update % :headers (fn [h] (assoc-in h [k :enabled] v)))))
     :set-schemas (fn [d] (set-filters #(update % :schemas (fn [v] (merge v d)))))}))

(defn ->qualified-name [table]
  (str/upper-case (str (:catalog table) "." (:schema table) "." (:table table))))

(defn unique-by [pred coll]
  (reduce
   (fn [acc item]
     (if-not (some pred acc)
       (conj acc item)
       acc))
   [] coll))


(defn ->table-row [{:keys [sources transforms] :as table}]
  (-> table
      ;; (update :created format-date)
      (update :primary-keys #(str/join ", " (map :column %)))
      (assoc :extracts (some->> sources
                                (unique-by :extract-id)
                                (mapv #(-> {:id        (:extract-id %)
                                            :source-id (:source-id %)
                                            :name      (:name %)}))))
      (assoc :sources (some->> sources
                               (unique-by :source-id)
                               (mapv #(-> {:id   (:source-id %)
                                           :name (:source-name %)}))))
      (assoc :transforms (some->> transforms
                                  (unique-by :master-id)
                                  (mapv #(-> {:id   (:master-id %)
                                              :name (:name %)}))))
      (assoc :groups (some->> transforms
                              (unique-by :group-id)
                              (mapv #(-> {:id   (:group-id %)
                                          :name (:group-name %)}))))))

(defn dropdown-button [{:keys [label value class]} children]
  [popover-root
   [popover-trigger {:as-child true}
    [:div
     [typography/label label]
     [:div {:class (clsx (select-trigger-class {}) "pr-2")}
      [:div {:class "flex justify-between items-center gap-2 min-w-[140px]"}
       value
       [:> ChevronDown {:size 20}]]]]]

   [popover-content {:class class}
    [:div
     [:> RadixScrollArea/Root
      [:> RadixScrollArea/Viewport {:class "h-full"
                                    :style {:max-height "min(var(--radix-popover-content-available-height), 400px)"
                                            :overflowY  js/undefined}}
       children]
      [:> RadixScrollArea/Scrollbar {:orientation "vertical" :class "w-3 px-0.5 py-1" :style {:box-sizing "unset"}}
       [:> RadixScrollArea/Thumb {:class "w-full bg-kleene-neutral-02 dark:bg-kleene-dark-03 rounded-md"}]]]]]])

(defn tables-controls [{:keys [filters set-query set-schemas set-headers]}]
  (let [headers        (:headers filters)
        schemas        (:schemas filters)
        schemas-groups (->> schemas
                            (reduce (fn [acc [name v]]
                                      (let [[catalog schema] (str/split name #"\.")]
                                        (update acc catalog
                                                #(conj % [schema v]))))
                                    {})
                            (into [])
                            (sort-by first))

        schemas-on-count (->> schemas (map second) (filter identity) count)
        headers-on-count (->> headers (map second) (filter :enabled) count)

        all-headers?     (= headers-on-count (count headers))
        visible-headers  (filter (comp :enabled second) headers)]

    [:div {:class "flex justify-between items-end"}
     [:div {:class "flex gap-2"}
      [fields/input {:label     "Search"
                     :icon      Search
                     :value     (:query filters)
                     :on-change #(set-query (jsi/get-in % [:target :value] ""))
                     :class     "min-w-[250px]"}]

      [dropdown-button {:label "Filter by schema"
                        :value (if (= schemas-on-count (count schemas))
                                 "All schemas"
                                 (str schemas-on-count " schemas"))
                        :class "px-0 pb-0"}
       [:div
        [checkbox {:label     "All schemas"
                   :checked   (every? true? (map second (:schemas filters)))
                   :on-change #(set-schemas (into {} (mapv (fn [[k _]] [k %]) schemas)))
                   :classes   {:root "px-3 w-full"}}]
        [:hr {:class "mt-2"}]
        [accordion-root {:type :multiple}

         (for [[catalog groups] schemas-groups]
           ^{:key catalog}
           [accordion-item {:value catalog}
            [accordion-trigger {:reversed true :class "px-2"}
             [checkbox {:label   catalog
                        :checked (every? true? (mapv second groups))
                        :on-click (fn [e]
                                    (.preventDefault e)
                                    (let [v (not (every? true? (mapv second groups)))]
                                      (set-schemas (into {} (mapv (fn [[schema]]
                                                                    [(str catalog "." schema) v])
                                                                  groups)))))}]]
            [accordion-content
             [:div {:class "pl-10 pr-2 flex flex-col gap-2 pb-2"}
              (for [[schema enabled] groups]
                ^{:key schema}
                [checkbox {:label   schema
                           :checked enabled
                           :on-change (fn [v]
                                        (set-schemas {(str catalog "." schema) v}))}])]]])]]]

      [dropdown-button {:label "Select fields"
                        :value (if all-headers?
                                 "All fields"
                                 (str (count visible-headers) " fields selected"))
                        :class "px-0 pb-0"}
       [:div {:class "flex flex-col gap-2 px-3"}
        [checkbox {:label     "All fields"
                   :checked   all-headers?
                   :on-change #(mapv (fn [[k _]] (set-headers k %)) headers)}]
        [:hr {:class "-mx-2"}]
        (for [[header {:keys [enabled]}] (sort-by (comp :order second) headers)]
          ^{:key header}
          [checkbox {:label     header
                     :checked   enabled
                     :on-change #(set-headers header %)}])]]]
     [:div
      [button {:variant  :empty
               :icon     RefreshCw
               :on-click init-tables}
       "Refresh"]]]))

(defn unit-tests->str [tests]
  (let [tests-no        (count tests)
        failed-tests    (filter #(false? (:result %)) tests)
        passed-tests-no (- tests-no (count failed-tests))]
    (str passed-tests-no "/" tests-no)))

(defn unit-tests-cell [{:keys [value]}]
  (when-let [unit-tests (js->clj value :keywordize-keys true)]
    (let [tests-no        (count unit-tests)
          failed-tests    (filter #(false? (:result %)) unit-tests)
          passed-tests-no (- tests-no (count failed-tests))
          passed-ratio    (/ passed-tests-no tests-no)
          color           (cond
                            (= tests-no passed-tests-no) "bg-kleene-text-success"
                            (> 0.5 passed-ratio)         "bg-kleene-text-error"
                            :else                        "bg-kleene-warning-03")
          tooltip-content [:span {:class "text-xs whitespace-pre-line"}
                           (if (= tests-no passed-tests-no)
                             "Passed all the latest tests in the past week."
                             [:<>
                              [typography/h4 "Failed unit tests:"]
                              (str/join "\n" (mapv (fn [{:keys [timestamp type description]}]
                                                     (str (format-date timestamp) " - " type " - " description))
                                                   (reverse failed-tests)))])]]
      [tooltip {:content tooltip-content}
       [:div {:class "flex gap-1 items-center cursor-help"}
        [:div {:class (clsx "w-2 h-2 rounded-full bg-kleene-text-success" color)}]
        [:span {:class "text-xs"} (str passed-tests-no "/" tests-no " passed")]]])))

(defn extracts-cell [{:keys [value]}]
  (when-let [items (js->clj value :keywordize-keys true)]
    [:div {:class "flex flex-col"}
     (for [{:keys [id source-id name]} items]
       [:a {:key    id
            :href   (rt.easy/href :extract-details {:source-id source-id :extract-id id})
            :target "_blank"
            :class  "hover:underline"}
        name])]))

(defn sources-cell [{:keys [value]}]
  (when-let [items (js->clj value :keywordize-keys true)]
    [:div {:class "flex flex-col"}
     (for [{:keys [id name]} items]
       ^{:key id}
       [:a {:href   (rt.easy/href :source-details {:source-id id})
            :target "_blank"
            :class  "hover:underline"}
        name])]))

(defn transforms-cell [{:keys [value]}]
  (when-let [items (js->clj value :keywordize-keys true)]
    [:div {:class "flex flex-col"}
     (for [{:keys [id name]} items]
       ^{:key id}
       [:a {:href   (rt.easy/href :dynamic-docs-details {:entity-id id})
            :target "_blank"
            :class  "hover:underline"}
        name])]))

(defn groups-cell [{:keys [value]}]
  (when-let [items (js->clj value :keywordize-keys true)]
    [:div {:class "flex flex-col"}
     (for [{:keys [id name]} items]
       ^{:key id}
       [:a {:href   (rt.easy/href :transforms {:group-id id})
            :target "_blank"
            :class  "hover:underline"}
        name])]))

(defn external-connection-cell [{:keys [value]}]
  (when value
    [:div {:class "flex justify-center"}
     [:> Check {:class "text-kleene-text-success"}]]))

(defn created-cell [{:keys [value]}]
  [date-tooltip {:date value}])

(defn- reagent-cell-renderer [component]
  (fn [props]
    (reagent/as-element
     [component {:value (jsi/get props :value)}])))

(def ^:private unit-tests-renderer (reagent-cell-renderer unit-tests-cell))
(def ^:private extracts-cell-renderer (reagent-cell-renderer extracts-cell))
(def ^:private sources-cell-renderer (reagent-cell-renderer sources-cell))
(def ^:private transforms-cell-renderer (reagent-cell-renderer transforms-cell))
(def ^:private groups-cell-renderer (reagent-cell-renderer groups-cell))
(def ^:private created-cell-renderer (reagent-cell-renderer created-cell))
(def ^:private external-connection-cell-renderer
  (reagent-cell-renderer external-connection-cell))

(def ^:private tables-theme-overrides
  {:headerFontWeight 500
   :headerFontSize "14px"
   :cellHorizontalPadding "12px"})

(def ^:private tables-theme-light
  (jsi/call ag-theme/light :withParams
            (clj->js (merge ag-theme/kleene-token-params tables-theme-overrides))))

(def ^:private tables-theme-dark
  (jsi/call ag-theme/dark :withParams
            (clj->js (merge ag-theme/kleene-token-params tables-theme-overrides))))

(defn- row-value [params field]
  (some-> (jsi/get params :data)
          (jsi/get field)))

(defn- unit-tests-filter-value [params]
  (when-some [value (row-value params "unit-tests")]
    (unit-tests->str (js->clj value :keywordize-keys true))))

(defn- created-filter-value [params]
  (some-> (row-value params "created")
          format-date))

(defn- relationship-filter-value [field]
  (fn [params]
    (when-some [value (row-value params field)]
      (->> (js->clj value :keywordize-keys true)
           (mapv :name)
           (str/join ", ")))))

(def ^:private tables-column-defs
  (clj->js
   [{:field "table" :headerName "Table name" :width 300
     :pinned "left" :lockPinned true :lockPosition true
     :cellStyle {:cursor "pointer"}}
    {:field "unit-tests" :headerName "Unit tests" :width 150
     :cellRenderer unit-tests-renderer
     :filterValueGetter unit-tests-filter-value
     :cellStyle {:cursor "pointer"}}
    {:field "schema" :headerName "Schema" :width 300
     :cellStyle {:cursor "pointer"}}
    {:field "catalog" :headerName "Catalog" :width 300
     :cellStyle {:cursor "pointer"}}
    {:field "created" :headerName "Created at" :width 200
     :cellRenderer created-cell-renderer
     :filterValueGetter created-filter-value
     :cellStyle {:cursor "pointer"}}
    {:field "primary-keys" :headerName "Primary keys" :width 150
     :cellStyle {:cursor "pointer"}}
    {:field "row-count" :headerName "Row count" :width 150
     :type "numericColumn" :cellStyle {:cursor "pointer"}}
    {:field "external-connection" :headerName "Ext. connect." :width 150
     :cellRenderer external-connection-cell-renderer
     :cellStyle {:cursor "pointer"}}
    {:field "extracts" :headerName "Output of extracts" :width 300
     :cellRenderer extracts-cell-renderer
     :filterValueGetter (relationship-filter-value "extracts")}
    {:field "sources" :headerName "Output of sources" :width 300
     :cellRenderer sources-cell-renderer
     :filterValueGetter (relationship-filter-value "sources")}
    {:field "transforms" :headerName "Output of transforms" :width 300
     :cellRenderer transforms-cell-renderer
     :filterValueGetter (relationship-filter-value "transforms")}
    {:field "groups" :headerName "Output of transform groups" :width 300
     :cellRenderer groups-cell-renderer
     :filterValueGetter (relationship-filter-value "groups")}]))

(def ^:private tables-default-col-def
  #js {:editable false
       :filter condition-value-filter/condition-value-filter
       :resizable true
       :sortable false
       :suppressMovable true})

(defn- apply-column-visibility! [api headers]
  (doseq [[_ {:keys [enabled order]}] headers]
    (when-let [column-id (nth table-column-ids order nil)]
      (jsi/call api :setColumnsVisible #js [column-id] (boolean enabled)))))

(defn- on-table-cell-clicked [event]
  (let [column-id (jsi/call (jsi/get event :column) :getColId)
        data      (jsi/get event :data)]
    (when (and data (contains? details-column-ids column-id))
      (dispatch [:app.events/navigate
                 :table-details
                 nil
                 {:db     (jsi/get data "catalog")
                  :schema (jsi/get data "schema")
                  :table  (jsi/get data "table")}]))))

(defn tables-table [{:keys [data headers]}]
  (let [dark-mode? @(re-posh/subscribe [:app/prop :app/dark-mode?])
        api-ref    (react/useRef nil)
        row-data   (react/useMemo #(clj->js data) #js [data])
        {:keys [on-grid-ready on-column-resized]}
        (column-resize/use-auto-size-toggle false)]
    (react/useEffect
     (fn []
       (when-let [api (jsi/get api-ref :current)]
         (apply-column-visibility! api headers))
       js/undefined)
     #js [headers])

    [:div {:style {:height "calc(100vh - 130px)"
                   :width  "100%"}}
     [:> AgGridReact
      {:theme                    (if dark-mode? tables-theme-dark tables-theme-light)
       :row-data                 row-data
       :column-defs              tables-column-defs
       :default-col-def          tables-default-col-def
       :row-height               32
       :suppress-no-rows-overlay true
       :on-cell-clicked           on-table-cell-clicked
       :on-column-resized         on-column-resized
       :on-grid-ready
       (fn [event]
         (jsi/assoc! api-ref :current (jsi/get event :api))
         (apply-column-visibility! (jsi/get event :api) headers)
         (on-grid-ready event))}]]))

(defn page* []
  (let [{:keys [tables filters set-schemas set-headers set-query]} (use-table-filters)

        rows     (react/useMemo #(mapv ->table-row tables) #js [tables])
        loading? @(re-posh/subscribe [:mold-http/loading? :tables-metadata.api/search])]
    [page-root
     [page-header {:class   "left-0 -top-12"
                   :variant :border}
      [page-title "Tables"]
      [page-controls
       [tables-controls {:filters     filters
                         :set-schemas set-schemas
                         :set-query   set-query
                         :set-headers set-headers}]]]
     [page-content {:variant :border :class "overflow-visible"}
      [:div {:class "relative z-0 h-full overflow-hidden"}
       (if loading?
         [:div {:class "w-full h-full flex items-center justify-center gap-2"}
          [spinner-lucide] "Loading..."]
         [:f> tables-table {:headers (:headers filters)
                            :data    rows}])]]]))

(defn page []
  [:f> page*])
